import * as B from '@blueprintjs/core';
import classnames from 'classnames';
import React from 'react';
import {CSSTransition, TransitionGroup} from 'react-transition-group';

import cs from './TabbedSidebar.module.scss';

export interface SidebarView {
  title: string;
  className?: string;
  icon: React.ReactElement;
  disabled?: boolean;
  /** Tooltip shown on hover when the view is disabled. */
  disabledTooltip?: string;
  /** Callback for rendering the view’s content if it’s being displayed. */
  render?: () => React.ReactNode;
  /** If a view has no content to render, you can hide the panel.*/
  hidePanelDrawer?: boolean;
  /** Callback to be executed on clicking the sidebar, separate from its render. */
  onClick?: () => void;
}

export interface SidebarViewState {
  currentViewTitle: string | null;
  isExpanded: boolean;
}

export const DEFAULT_SIDEBAR_VIEW_STATE: SidebarViewState = {
  currentViewTitle: null,
  isExpanded: false,
};

const ICON_SIZE = 24;

const SLIDE_CLASS_NAMES = {
  enter: cs.slideEnter,
  enterActive: cs.slideEnterActive,
  exit: cs.slideExit,
  exitActive: cs.slideExitActive,
};

const SIDEBAR_SLIDE_MS = 200;
const CONTENT_SLIDE_MS = 300;

const slideDurationStyle = (ms: number) => ({'--slideDuration': `${ms}ms`}) as React.CSSProperties;

// Copied in from TabbedSidebar.styl. We use hard-coded values because the
// slide animations make it tricky to use clientWidth to dynamically know the
// width of these elements as they’re updated.
export const SIDEBAR_WIDTH = 70; // 7rem
export const SIDEBAR_CONTENT_WIDTH = 360; //36rem

export const TOGGLE_SIDEBAR_BUTTON_TEST_ID = 'toggle-sidebar-button';

/**
 * Component for a left-hand set of tabs that have slide-out content. This
 * component is controlled, so you’ll need to pass state and setState.
 *
 * The currentViewTitle should always match one of the views, even if the
 * component is collapsed. That way if the user toggles the sidebar open it
 * opens to where they left off.
 */
const TabbedSidebar: React.FunctionComponent<
  React.PropsWithChildren<{
    hidden?: boolean;
    views: SidebarView[];

    state: SidebarViewState;
    setState: (s: SidebarViewState) => void;

    /**
     * Called when the size of the sidebar changes so that the map can update to
     * keep itself from being obscured.
     */
    setPaddingOptions?: (padding: mapboxgl.PaddingOptions) => void;
  }>
> = ({
  views,
  hidden = false,
  setPaddingOptions,
  state: {currentViewTitle, isExpanded},
  setState,
}) => {
  React.useLayoutEffect(() => {
    setPaddingOptions &&
      setPaddingOptions({
        top: 0,
        right: 0,
        bottom: 0,
        left: hidden // if sidebar is hidden, we need 0 padding
          ? 0
          : !isExpanded || views.find((view) => view.title === currentViewTitle)?.hidePanelDrawer
            ? SIDEBAR_WIDTH
            : SIDEBAR_WIDTH + SIDEBAR_CONTENT_WIDTH,
      });
  }, [isExpanded, setPaddingOptions, hidden, currentViewTitle, views]);

  // Defaulting to the 1st on "null" lets us have a common default state that
  // works for all values of the views prop.
  const currentView =
    (currentViewTitle && views.find(({title}) => title === currentViewTitle)) || views[0];

  const makeOnViewButtonClick = (title: string) => () => {
    if (title === currentView.title && isExpanded) {
      setState({
        isExpanded: false,
        currentViewTitle: currentView.title,
      });
    } else {
      setState({
        isExpanded: true,
        currentViewTitle: title,
      });
    }
  };

  const disableToggle = !currentView || currentView.disabled;

  React.useEffect(() => {
    if (currentView.onClick && isExpanded) {
      currentView.onClick();
    }
  }, [currentView, isExpanded]);

  const sidebarContainerRef = React.useRef<HTMLDivElement>(null);
  const sidebarContentRef = React.useRef<HTMLDivElement>(null);

  return (
    <>
      <TransitionGroup>
        {!hidden && (
          <CSSTransition
            key="sidebar"
            nodeRef={sidebarContainerRef}
            classNames={SLIDE_CLASS_NAMES}
            timeout={SIDEBAR_SLIDE_MS}
          >
            <div
              ref={sidebarContainerRef}
              className={cs.sidebarContainer}
              style={slideDurationStyle(SIDEBAR_SLIDE_MS)}
            >
              <div className={cs.sidebar}>
                <div className={cs.buttonGroup}>
                  {views.map(
                    ({title, className, icon: IconComponent, disabled, disabledTooltip}) => {
                      const modifiedIcon = React.cloneElement(IconComponent, {
                        size: ICON_SIZE,
                      });
                      const button = (
                        <B.AnchorButton
                          key={title}
                          className={classnames(cs.button, className)}
                          onClick={makeOnViewButtonClick(title)}
                          active={isExpanded && title === currentView.title && !disabled}
                          disabled={disabled}
                          minimal
                        >
                          {modifiedIcon}
                          {title}
                        </B.AnchorButton>
                      );
                      if (disabled && disabledTooltip) {
                        return (
                          <B.Tooltip key={title} content={disabledTooltip} position="right" fill>
                            {button}
                          </B.Tooltip>
                        );
                      }
                      return button;
                    }
                  )}
                </div>

                <B.Button
                  className={cs.button}
                  minimal
                  // Have to use testid because the icon’s title gets stuck in a <desc>
                  // element.
                  data-testid={TOGGLE_SIDEBAR_BUTTON_TEST_ID}
                  onClick={() =>
                    setState({isExpanded: !isExpanded, currentViewTitle: currentView.title})
                  }
                  disabled={disableToggle}
                  style={{
                    opacity: disableToggle ? 0 : 1,
                    transition: 'opacity 250ms',
                  }}
                >
                  <B.Icon
                    style={{
                      transform: `rotateY(${isExpanded ? '180deg' : '0'})`,
                      transition: 'transform 250ms',
                    }}
                    icon="double-chevron-right"
                    iconSize={ICON_SIZE}
                    title={isExpanded ? 'Collapse sidebar' : 'Expand sidebar'}
                  />
                </B.Button>
              </div>

              <TransitionGroup>
                {isExpanded &&
                  currentView &&
                  !currentView.disabled &&
                  !currentView.hidePanelDrawer &&
                  !!currentView.render && (
                    <CSSTransition
                      key="content"
                      nodeRef={sidebarContentRef}
                      classNames={SLIDE_CLASS_NAMES}
                      timeout={CONTENT_SLIDE_MS}
                    >
                      <div
                        ref={sidebarContentRef}
                        className={cs.sidebarContent}
                        style={slideDurationStyle(CONTENT_SLIDE_MS)}
                      >
                        {currentView.render()}
                      </div>
                    </CSSTransition>
                  )}
              </TransitionGroup>
            </div>
          </CSSTransition>
        )}
      </TransitionGroup>
    </>
  );
};

/** Helper wrapper for those times when you want an uncontrolled TabbedSidebar. */
export const WithTabbedSidebarState: React.FunctionComponent<{
  initialState?: SidebarViewState;
  children: (state: SidebarViewState, setState: (s: SidebarViewState) => void) => JSX.Element;
}> = ({initialState = DEFAULT_SIDEBAR_VIEW_STATE, children}) => {
  const [state, setState] = React.useState(initialState);
  return children(state, setState);
};

export default TabbedSidebar;
